import { useMemo } from "react";
import { authClient } from "@/lib/auth-client";
import { api } from "@/lib/api";
import type { EnrichedSubscriptionBase } from "nautika-types";

export function useSubscriptionStatus() {
  const { data: activeOrg } = authClient.useActiveOrganization();

  // Utiliser api.useQuery pour gérer le data fetching
  const {
    data: subscriptions,
    isLoading,
    error,
    refetch,
  } = api.useQuery(
    "get",
    "/subscriptions/list",
    {},
    {
      enabled: !!activeOrg, // Ne faire la requête que si on a une organisation active
      staleTime: 30 * 1000, // 30 secondes de cache
    }
  );

  // Calculer le statut de l'abonnement côté client
  const subscriptionStatus = useMemo(() => {
    if (!subscriptions || !Array.isArray(subscriptions)) {
      return {
        hasValidSubscription: false,
        isTrialExpired: true,
        isActive: false,
        subscription: null as EnrichedSubscriptionBase | null,
        trialEndDate: undefined as Date | undefined,
      };
    }

    const activeSubscription = subscriptions.find((sub) => sub.status === "active" || sub.status === "trialing");

    if (!activeSubscription) {
      return {
        hasValidSubscription: false,
        isTrialExpired: true,
        isActive: false,
        subscription: null as EnrichedSubscriptionBase | null,
        trialEndDate: undefined as Date | undefined,
      };
    }

    // Logique pour déterminer le statut
    if (activeSubscription.status === "trialing") {
      let trialEndDate: Date | null = null;

      if (activeSubscription.stripeData) {
        trialEndDate = new Date(Number(activeSubscription.stripeData.trialEnd) * 1000);
      }

      if (trialEndDate) {
        const now = new Date();
        const isTrialExpired = trialEndDate <= now;

        return {
          hasValidSubscription: !isTrialExpired,
          isTrialExpired,
          isActive: false,
          subscription: activeSubscription as EnrichedSubscriptionBase,
          trialEndDate,
        };
      }

      return {
        hasValidSubscription: true,
        isTrialExpired: false,
        isActive: false,
        subscription: activeSubscription as EnrichedSubscriptionBase,
        trialEndDate: undefined,
      };
    }

    if (activeSubscription.status === "active") {
      return {
        hasValidSubscription: true,
        isTrialExpired: false,
        isActive: true,
        subscription: activeSubscription as EnrichedSubscriptionBase,
        trialEndDate: undefined,
      };
    }

    return {
      hasValidSubscription: false,
      isTrialExpired: false,
      isActive: false,
      subscription: activeSubscription as EnrichedSubscriptionBase,
      trialEndDate: undefined,
    };
  }, [subscriptions]);

  return {
    ...subscriptionStatus,
    isLoading,
    error,
    refetch,
  };
}
